import React, { useState } from "react";
import * as AlertDialog from "@radix-ui/react-alert-dialog";
import {
  Outlet,
  Link,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import {
  Home,
  Camera,
  FileText,
  Upload,
  MapPin,
  AlertTriangle,
  LogOut,
  Menu,
  X,
  User,
  BookOpen,
  ScanEye,
  Activity,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { GlobalStatsBar } from "@/components/common/global-stats-bar";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";

interface LayoutProps {
  children?: React.ReactNode;
}

type NavItem = {
  name: string;
  href: string;
  icon: typeof Home;
  badge?: string | number;
};

const navigationItems: NavItem[] = [
  { name: "Dashboard", href: "/dashboard", icon: Home },
  { name: "Crossing review", href: "/loop", icon: Camera },
  { name: "Sessions", href: "/sessions", icon: FileText },
  { name: "Orphans", href: "/orphans", icon: AlertTriangle },
  { name: "Camera Audits", href: "/camera-audits", icon: Activity },
  { name: "Misreads", href: "/misreads", icon: ScanEye },
  { name: "State Analysis", href: "/state-analytics", icon: MapPin },
  { name: "Upload", href: "/upload", icon: Upload },
  { name: "User Guide", href: "/user-guide", icon: BookOpen },
];

export function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingClient, setPendingClient] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, clients, activeClient, switchClient, accessEnabled } =
    useAuth();
  const isLoop = location.pathname.startsWith("/loop");

  const isAccount = location.pathname === "/account";
  const isAccess = location.pathname === "/access";
  const client = clients.find((c) => c.id === activeClient);
  const nav = [
    ...navigationItems.filter(
      (item) => activeClient === "omaha" || item.href === "/loop",
    ),
    ...(accessEnabled && user?.is_superuser
      ? [{ name: "Clients & access", href: "/access", icon: User }]
      : []),
    ...(accessEnabled
      ? [{ name: "My account", href: "/account", icon: User }]
      : []),
  ];
  if (user?.must_change_password && !isAccount)
    return <Navigate to="/account" replace />;
  if (activeClient !== "omaha" && !isLoop && !isAccount && !isAccess)
    return <Navigate to="/loop" replace />;

  const handleLogout = () => {
    // TODO: Implement logout logic
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background">
      <AlertDialog.Root
        open={pendingClient !== null}
        onOpenChange={(open) => {
          if (!open) setPendingClient(null);
        }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 z-[80] bg-black/60" />
          <AlertDialog.Content className="fixed left-1/2 top-1/2 z-[90] w-[min(90vw,420px)] -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-card p-6 shadow-xl">
            <AlertDialog.Title className="text-lg font-semibold">
              Switch to {clients.find((c) => c.id === pendingClient)?.name}?
            </AlertDialog.Title>
            <AlertDialog.Description className="my-4 text-sm text-muted-foreground">
              Unsaved review notes will be discarded.
            </AlertDialog.Description>
            <div className="flex justify-end gap-2">
              <AlertDialog.Cancel asChild>
                <Button variant="outline">Stay here</Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action asChild>
                <Button
                  onClick={() => pendingClient && switchClient(pendingClient)}
                >
                  Switch client
                </Button>
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 transform bg-card border-r border-border transition-transform duration-200 ease-in-out lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between px-6 border-b border-border">
            <Link
              to="/dashboard"
              aria-label="Loop home"
              className="flex flex-col gap-2"
            >
              <img
                src="/brand/verge-lockup-on-dark.svg"
                alt="Verge"
                className="w-36 h-auto"
              />
              <span className="text-[10px] tracking-[.16em] text-muted-foreground">
                LOOP
              </span>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="px-4 pt-4">
            {accessEnabled && user?.is_superuser ? (
              <label className="text-xs text-muted-foreground">
                Client
                <select
                  aria-label="Active client"
                  value={activeClient}
                  onChange={(e) => setPendingClient(e.target.value)}
                  className="mt-1 w-full rounded border bg-background p-2 text-sm text-foreground"
                >
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <p className="font-medium">
                {client?.name ||
                  (user?.must_change_password ? "Account setup" : "Omaha")}
              </p>
            )}
          </div>
          {/* Navigation */}
          <nav className="flex-1 space-y-2 p-4">
            {nav.map((item) => {
              const Icon = item.icon;
              const isActive =
                location.pathname === item.href ||
                (item.href === "/loop" && isLoop);

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-muted hover:text-foreground",
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground",
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <div className="flex items-center space-x-3">
                    <Icon className="h-4 w-4" />
                    <span>{item.name}</span>
                  </div>
                  {item.badge && (
                    <Badge variant="secondary" className="text-xs">
                      {item.badge}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="border-t border-border p-4">
            <div className="flex items-center space-x-3 mb-3">
              <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                <User className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {user?.username || "Signed in"}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {user?.email || "Shared account"}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="w-full"
            >
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Top bar */}
        <header className="sticky top-0 z-40 flex h-16 items-center gap-x-4 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 sm:gap-x-6 sm:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>

          <div className="flex flex-1 items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              {isLoop ? "Loop / Crossing review" : "Loop / Operations"}
            </p>
            <span className="text-xs text-muted-foreground">
              {user?.username}
            </span>
          </div>
        </header>

        {import.meta.env.VITE_LOOP_PREVIEW === "true" && (
          <div className="px-6 py-3 text-xs text-primary border-b border-primary/20 bg-primary/5">
            LOCAL PREVIEW · Real imported Loop evidence. LPR services are not
            connected here.
          </div>
        )}
        {/* Global Stats Bar */}
        {!isLoop && !isAccount && !isAccess && activeClient === "omaha" && (
          <GlobalStatsBar />
        )}

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">{children || <Outlet />}</main>
      </div>
    </div>
  );
}
